import { dayOf, diffDays } from "../../../utils/weekDates";
import { sortByAccessor } from "../../../utils/sortRows";
import { columnKeyFor, getFieldValue, plainCustomValue } from "../../../utils/customFieldUtils";

export const PRIORITIES = ["high", "medium", "low"];
export const PRIORITY_STYLE = {
  high: { bg: "#fee2e2", fg: "#991b1b" },
  medium: { bg: "#fef3c7", fg: "#92400e" },
  low: { bg: "#dcfce7", fg: "#166534" },
};

// Status groups in the order the list shows them; dot colours the heading.
export const GROUPS = {
  overdue: { label: "Overdue", dot: "#e53935" },
  progress: { label: "In progress", dot: "#185FA5" },
  todo: { label: "To do", dot: "#9ca3af" },
  upcoming: { label: "Upcoming", dot: "#85B7EB" },
  done: { label: "Completed", dot: "#16a34a" },
};
export const GROUP_ORDER = Object.values(GROUPS).map((g) => g.label);

export const TASK_COLUMNS = [
  { key: "title", label: "Task", visible: true },
  { key: "category", label: "Category", visible: true },
  { key: "assignee", label: "Assignee", visible: true },
  { key: "starts", label: "Starts", visible: true },
  { key: "due", label: "Due", visible: true },
  { key: "priority", label: "Priority", visible: true },
  { key: "repeat", label: "Repeats", visible: false },
  { key: "description", label: "Description", visible: false },
  { key: "completed", label: "Completed", visible: false },
];

const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export const TASK_GROUPINGS = {
  status: { label: "Status", get: (t) => GROUPS[t.group].label },
  assignee: { label: "Assignee", get: (t) => t.assignee_name || "Unassigned" },
  category: { label: "Category", get: (t) => t.category },
  priority: { label: "Priority", get: (t) => capital(t.priority) },
};

export const DEFAULT_FILTERS = { search: "", person: "", category: "", priority: "", groupBy: "status", sort: null };

export const ordinal = (n) => {
  const v = n % 100;
  return n + (["th", "st", "nd", "rd"][(v - 20) % 10] || ["th", "st", "nd", "rd"][v] || "th");
};
export const dayText = (day) => (day === "last" ? "last day" : ordinal(Number(day)));
export const repeatShort = (series) => `Monthly · ${dayText(series.day_of_month)}`;
export const scheduleText = (series) => `Monthly, ${dayText(series.day_of_month)} at ${series.start_time}`;
export const dueRuleText = (offset) => (Number(offset) === 0 ? "Due the same day" : `Due ${offset} day${Number(offset) === 1 ? "" : "s"} after start`);

export const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

// Same rule as the server: the day falls back to the month's last day when the month is shorter.
export const occurrence = (day, year, month) => {
  const last = new Date(year, month + 1, 0).getDate();
  const d = new Date(year, month, day === "last" ? last : Math.min(Number(day), last));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// The next three starts the form previews for a monthly schedule, from `from` (an open task's start, or today).
export const previewStarts = (day, from) => {
  const d = new Date(`${from}T00:00:00`);
  const here = occurrence(day, d.getFullYear(), d.getMonth());
  const shift = here >= from ? 0 : 1;
  return [0, 1, 2].map((k) => occurrence(day, d.getFullYear(), d.getMonth() + shift + k));
};

// Which status group a task sits in today: not-yet-started recurring tasks are Upcoming whatever their status.
export const groupOf = (t, today) => {
  if (t.status === "done") return "done";
  if (t.starts_on && t.starts_on > today) return "upcoming";
  if (t.due_on < today) return "overdue";
  return t.status;
};

export const normalizeTask = (t, today) => ({ ...t, group: groupOf(t, today), custom_fields: t.custom_fields || {} });

export const dueInfo = (t, today) => {
  if (t.status === "done") return { text: "Archived", sub: t.done_at ? `on ${shortDate(dayOf(t.done_at))}` : "", tone: "muted" };
  const late = diffDays(t.due_on, today);
  if (late > 0) return { text: shortDate(t.due_on), sub: `${late} day${late === 1 ? "" : "s"} late`, tone: "late" };
  return { text: shortDate(t.due_on), sub: late === 0 ? "Today" : "", tone: late === 0 ? "today" : "" };
};

const includes = (text, q) => (text || "").toLowerCase().includes(q);

export const filterTasks = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((t) =>
    (!f.person || String(t.assignee_employee_id) === f.person)
    && (!f.category || t.category === f.category)
    && (!f.priority || t.priority === f.priority)
    && (!q || includes(t.title, q) || includes(t.description, q))
  );
};

const ACCESSORS = {
  title: (t) => t.title,
  category: (t) => t.category,
  assignee: (t) => t.assignee_name,
  starts: (t) => t.starts_on,
  due: (t) => t.due_on,
  priority: (t) => PRIORITIES.indexOf(t.priority),
  repeat: (t) => (t.series ? repeatShort(t.series) : null),
  description: (t) => t.description || null,
  completed: (t) => t.done_at,
};

const accessorsFor = (fields) => ({
  ...ACCESSORS,
  ...Object.fromEntries(fields.map((field) => [columnKeyFor(field), (t) => plainCustomValue(field, getFieldValue(t, field))])),
});

// Soonest due first until a header is clicked; Completed reads most recent first.
const openKey = (t) => (t.group === "upcoming" ? t.starts_on : t.due_on);
export const sortTasks = (rows, sort, fields) => {
  if (sort) return sortByAccessor(rows, sort, accessorsFor(fields)[sort.key]);
  return [...rows].sort((a, b) => {
    const doneA = a.group === "done";
    const doneB = b.group === "done";
    if (doneA !== doneB) return doneA ? 1 : -1;
    return doneA ? String(b.done_at).localeCompare(String(a.done_at)) : openKey(a).localeCompare(openKey(b));
  });
};

export const exportColumns = (columns, fields) => {
  const accessors = accessorsFor(fields);
  const values = { ...accessors, priority: (t) => capital(t.priority), title: (t) => t.title };
  return columns.map((c) => ({ key: c.key, label: c.label, visible: c.visible, value: values[c.key] }));
};

// Done subtasks count, for the "1/3" pill.
export const subtaskProgress = (subtasks) => `${subtasks.filter((s) => s.done).length}/${subtasks.length}`;
